import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Button,
  Tooltip,
  Avatar,
  Container,
  Badge,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LanguageIcon from '@mui/icons-material/Language';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { logout } from '@store/slices/authSlice';
import MiniCart from '../common/MiniCart';

/**
 * Header component with navigation, language selector, and user menu
 * @param {Object} props - Component props
 * @param {number} props.drawerWidth - Width of the sidebar drawer
 * @param {Function} props.handleDrawerToggle - Function to toggle the drawer
 * @returns {JSX.Element} The Header component
 */
const Header = ({ drawerWidth, handleDrawerToggle }) => {
  const { t: tCommon, i18n } = useTranslation('common');
  const { t: tAuth } = useTranslation('auth');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { items } = useSelector((state) => state.cart);

  // State for user menu
  const [anchorElUser, setAnchorElUser] = useState(null);
  // State for language menu
  const [anchorElLang, setAnchorElLang] = useState(null);

  // Check if the user is an admin
  const isAdmin = user?.role === 'admin';

  // Available languages - Added 'label'
  const languages = [
    { code: 'en', label: 'EN', name: 'English' },
    { code: 'de', label: 'DE', name: 'Deutsch' },
    { code: 'fr', label: 'FR', name: 'Français' },
    { code: 'it', label: 'IT', name: 'Italiano' },
  ];

  /**
   * Handle opening the user menu
   * @param {Event} event - The click event
   */
  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  /**
   * Handle closing the user menu
   */
  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  /**
   * Handle opening the language menu
   * @param {Event} event - The click event
   */
  const handleOpenLangMenu = (event) => {
    setAnchorElLang(event.currentTarget);
  };

  /**
   * Handle closing the language menu
   */
  const handleCloseLangMenu = () => {
    setAnchorElLang(null);
  };

  /**
   * Handle changing the language
   * @param {string} langCode - The language code to change to
   */
  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    handleCloseLangMenu();
  };

  /**
   * Handle user logout
   */
  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
    handleCloseUserMenu();
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        width: { lg: `calc(100% - ${user ? drawerWidth : 0}px)` },
        ml: { lg: `${user ? drawerWidth : 0}px` },
      }}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          {/* Mobile menu button */}
          {user && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { lg: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Logo Text */}
          <Typography
            component={Link}
            to="/"
            variant="h6"
            noWrap
            sx={{
              mr: 2,
              fontFamily: "'Playfair Display', serif",
              fontWeight: 700,
              letterSpacing: '.1rem',
              color: 'inherit',
              textDecoration: 'none',
              fontSize: '1.5rem',
            }}
          >
            CONSCIENCE
          </Typography>

          {/* Spacer */}
          <Box sx={{ flexGrow: 1 }} />

          {/* Language selector */}
          <Box sx={{ mr: 2 }}>
            <Button
              onClick={handleOpenLangMenu}
              color="inherit"
              endIcon={<KeyboardArrowDownIcon />}
              aria-controls="menu-language"
              aria-haspopup="true"
              aria-expanded={Boolean(anchorElLang) ? 'true' : undefined}
              sx={{ textTransform: 'none' }}
            >
              {/* Display the label (e.g., EN) */}
              {languages.find((lang) => lang.code === i18n.language)?.label || languages[0].label}
            </Button>
            <Menu
              sx={{ mt: '30px' }}
              id="menu-language"
              anchorEl={anchorElLang}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorElLang)}
              onClose={handleCloseLangMenu}
              PaperProps={{
                sx: {
                  bgcolor: 'background.paper',
                  borderRadius: '0.375rem',
                  border: '1px solid',
                  borderColor: 'grey.200',
                  boxShadow:
                    '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
                  mt: '2px',
                },
              }}
            >
              {languages.map((lang) => {
                const isSelected = i18n.language === lang.code;
                const selectedColor = 'rgb(191 174 143)';

                return (
                  <MenuItem
                    key={lang.code}
                    selected={isSelected}
                    onClick={() => changeLanguage(lang.code)}
                    sx={{
                      ...(isSelected && {
                        bgcolor: 'rgba(191, 174, 143, 0.08)',
                      }),
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontWeight: 'medium',
                        mr: 1,
                        color: isSelected ? selectedColor : 'text.primary',
                      }}
                    >
                      {lang.label}
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      {lang.name}
                    </Box>
                  </MenuItem>
                );
              })}
            </Menu>
          </Box>

          {/* Cart icon - only show if user is not admin */}
          {user && !isAdmin && (
            <Box sx={{ mr: 2 }}>
              <MiniCart />
            </Box>
          )}

          {/* User menu */}
          {user ? (
            <Box sx={{ flexShrink: 0 }}>
              <Tooltip title={tAuth('account')}>
                <IconButton
                  onClick={handleOpenUserMenu}
                  sx={{ p: 0 }}
                >
                  <Avatar
                    alt={user.name}
                    src="/static/images/avatar/2.jpg"
                  />
                </IconButton>
              </Tooltip>
              <Menu
                sx={{ mt: '45px' }}
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
              >
                <MenuItem
                  component={Link}
                  to="/account"
                  onClick={handleCloseUserMenu}
                >
                  <Typography textAlign="center">{tCommon('navigation.account')}</Typography>
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                  <Typography textAlign="center">{tAuth('logout')}</Typography>
                </MenuItem>
              </Menu>
            </Box>
          ) : (
            <Box sx={{ flexShrink: 0 }}>
              <Button
                component={Link}
                to="/login"
                color="inherit"
                sx={{
                  mr: 1,
                  '&:hover': {
                    color: 'secondary.main',
                  },
                }}
              >
                {tAuth('login')}
              </Button>
              <Button
                component={Link}
                to="/register"
                variant="contained"
                color="secondary"
              >
                {tAuth('register')}
              </Button>
            </Box>
          )}
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Header;
